import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthProvider";
import Axios from "../../Axios";
import Papa from "papaparse";
import Navbar2 from "../../navbar/Navbar2";
import BottomNavbar from "../../components/bottomNavbar/BottomNavbar";
import { ArrowLeft, Download, Filter, Search, User, ChevronDown, X, Send, MessageSquare, ThumbsUp, ThumbsDown } from "lucide-react";
import "./leadpage.css";
import { FaWhatsapp } from "react-icons/fa";
// Shadcn Components
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

export default function LeadPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userToken, allFbData } = useContext(AuthContext);
  const [leadsdata, setLeadsData] = useState([]);
  const [leadInfo, setLeadInfo] = useState([]);
  const [campaignInfo, setCampaignInfo] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  
  // WhatsApp message modal state
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageStatus, setMessageStatus] = useState({ success: false, error: null });
  const [updatingLeads, setUpdatingLeads] = useState(new Set());
  const [leadQualities, setLeadQualities] = useState(new Map()); // Track lead quality selections
  const [selectedLeadDetail, setSelectedLeadDetail] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Process Facebook campaign data
  useEffect(() => {
    if (allFbData && allFbData.length > 0) {
      const processedCampaigns = allFbData.map(campaign => ({
        id: campaign.id,
        name: campaign.name,
        status: campaign.status,
        dbId: campaign.dbId
      }));
      
      setCampaigns(processedCampaigns);
      
      // If we have a campaign from location state, use it
      const campaignFromState = location?.state;
      if (campaignFromState) {
        setSelectedCampaignId(campaignFromState);
      } else if (processedCampaigns.length > 0) {
        // Otherwise use the first campaign
        setSelectedCampaignId(processedCampaigns[0].id);
      }
    }
  }, [allFbData, location?.state]);

  // Fetch leads when selected campaign changes
  useEffect(() => {
    if (selectedCampaignId) {
      getAllLeadsForCampaign(selectedCampaignId);
    }
  }, [selectedCampaignId]);

  // Fetch WhatsApp templates when modal opens
  useEffect(() => {
    if (isMessageModalOpen) {
      fetchWhatsAppTemplates();
    }
  }, [isMessageModalOpen]);

  const fetchWhatsAppTemplates = async () => {
    try {
      const response = await Axios.get('/get-templates', {
        headers: {
          Authorization: userToken
        }
      });
      
      if (response.status === 200) {
        const templatesData = response.data.message?.data || [];
        setTemplates(templatesData);
        if (templatesData.length > 0) {
          setSelectedTemplate(templatesData[0].name);
        }
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error("Error fetching WhatsApp templates:", error);
      setTemplates([]);
    }
  };

  const getAllLeadsForCampaign = async (campaignId) => {
    setLoading(true);
    try {
      if (campaignId) {
        const res = await Axios.get("/get-all-leads", {
          headers: {
            Authorization: userToken,
          },
          params: {
            campaign_id: campaignId,
          },
        });

        if (res?.status === 200) {
          const data = res?.data?.message;

          if (data) {
            setCampaignInfo(data?.campaign_info);

            const leadvalue = data?.all_leads?.flat();
            if (leadvalue) {
              let leadsResult = [];
              let leadQualitiesMap = new Map();

              leadvalue.forEach((val) => {
                let innerlead = {};

                val?.field_data?.forEach((data) => {
                  innerlead[data?.name] = data?.values[0];
                  innerlead["created_time"] = val?.created_time;
                  innerlead["id"] = val?.id;
                  innerlead["comment"] = val?.comment;
                  innerlead["type"] = val?.type; // Include the type field
                });
                
                // Store the lead quality for highlighting
                if (val?.type) {
                  leadQualitiesMap.set(val?.id, val?.type);
                }
                
                if (Object.keys(innerlead)?.length) {
                  leadsResult.push(innerlead);
                }
              });

              if (leadsResult?.length > 0) {
                setLeadsData(leadsResult);
                // Update lead qualities state with existing types from API
                setLeadQualities(leadQualitiesMap);
              } else {
                setLeadsData([]);
                setLeadQualities(new Map());
              }
            } else {
              setLeadsData([]);
              setLeadQualities(new Map());
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
      setLeadsData([]);
      setLeadQualities(new Map());
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    handleDownloadCSV();
  };

  // for csv
  const DownloadcsvMethod = async (leadsResult) => {
    // Get a set of all unique keys in the array
    const allKeys = Array.from(
      new Set(leadsResult?.flatMap((obj) => Object.keys(obj)))
    );

    // Update each object to include all keys with values or null
    const updatedArray = leadsResult?.map((obj) => {
      const updatedObj = { ...obj };
      allKeys.forEach((key) => {
        if (!obj.hasOwnProperty(key)) {
          updatedObj[key] = null;
        }
      });
      return updatedObj;
    });
    if (updatedArray?.length) {
      downloadCsvByPap(updatedArray);
    }
  };

  const downloadCsvByPap = (data) => {
    const csv = Papa.unparse(data);
    // Create a Blob with the CSV data
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    // Create a link element and trigger the download
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", `#${campaignInfo?.name || selectedCampaignId}.csv`);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadCSV = async () => {
    if (leadsdata?.length > 0) {
      DownloadcsvMethod(leadsdata);
    }
  };

  const searchFilter = () => {
    const searchData = leadsdata?.filter((val) => {
      const values = Object.values(val);
      return values.some(
        (value) =>
          value &&
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    });

    return searchData;
  };

  const extractKeys = (data) => {
    if (!data?.length) return [];
    const excludedKeys = ["created_time", "id", "comment"];
    return Object.keys(data[0]).filter((key) => !excludedKeys.includes(key));
  };

  // Function to handle lead row click
  const handleLeadClick = async (leadId) => {
    try {
      const res = await Axios.get(`/get-lead-data?leadId=${leadId}`, {
        headers: {
          Authorization: userToken
        }
      });
      
      if (res.status === 200) {
        const leadDetail = res.data.message;
        setSelectedLeadDetail(leadDetail);
        setIsSidebarOpen(true);
      }
    } catch (error) {
      console.error("Error fetching lead details:", error);
    }
  };

  // Function to close sidebar
  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedLeadDetail(null);
  };

  // Function to format field names
  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const leadKeys = extractKeys(leadsdata);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Handle campaign change
  const handleCampaignChange = (campaignId) => {
    setSelectedCampaignId(campaignId);
    setLeadsData([]);
    setCampaignInfo(null);
  };

  // Open WhatsApp message modal
  const openMessageModal = (lead) => {
    setSelectedLead(lead);
    setIsMessageModalOpen(true);
    setMessageStatus({ success: false, error: null });
  };

  // Close WhatsApp message modal
  const closeMessageModal = () => {
    setIsMessageModalOpen(false);
    setSelectedTemplate("");
    setSelectedLead(null);
    setSendingMessage(false);
  };

  // Update lead quality function
  const updateLeadQuality = async (leadId, type) => {
    setUpdatingLeads(prev => new Set([...prev, leadId]));
    
    try {
      const body = {
        leadId: leadId,
        type: type
      };

      const response = await Axios.post("/update-lead", body, {
        headers: {
          Authorization: userToken
        }
      });
      
      if (response.status === 200 && response?.data?.valid) {
        // Update local state for visual feedback
        setLeadQualities(prev => new Map(prev.set(leadId, type)));
        
        console.log(`Lead ${leadId} updated successfully with type: ${type}`);
      } else {
        console.error("Failed to update lead:", response.data.message);
      }
    } catch (error) {
      console.error("Error updating lead:", error);
    } finally {
      setUpdatingLeads(prev => {
        const newSet = new Set(prev);
        newSet.delete(leadId);
        return newSet;
      });
    }
  };

  // Send WhatsApp message
  const sendWhatsAppMessage = async () => {
    if (!selectedTemplate || !selectedLead) return;
    
    setSendingMessage(true);
    setMessageStatus({ success: false, error: null });
    
    try {
      // Find phone number field - assuming it's either "phone_number", "phone" or any field containing "phone"
      let phoneNumber = null;
      const phoneFields = Object.keys(selectedLead).filter(key => 
        key.toLowerCase().includes('phone') || key.toLowerCase().includes('mobile')
      );
      
      if (phoneFields.length > 0) {
        phoneNumber = selectedLead[phoneFields[0]];
      }
      
      if (!phoneNumber) {
        setMessageStatus({ success: false, error: "No phone number found for this lead" });
        setSendingMessage(false);
        return;
      }
      
      // Format the phone number (remove any non-numeric characters)
      phoneNumber = phoneNumber.replace(/\D/g, '');
      
      const response = await Axios.post('/send-message', {
        template_name: selectedTemplate,
        phone_number: phoneNumber
      }, {
        headers: {
          Authorization: userToken,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 200) {
        setMessageStatus({ success: true, error: null });
      } else {
        setMessageStatus({ success: false, error: response.data.message || "Failed to send message" });
      }
    } catch (error) {
      console.error("Error sending WhatsApp message:", error);
      setMessageStatus({ 
        success: false, 
        error: error.response?.data?.message || "Failed to send message. Please try again." 
      });
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <div className="bg-background min-h-screen flex flex-col">
      <Navbar2 />
      
      <main className="flex-1 container mx-auto px-4 py-6">
        <div className="flex items-center mb-6">
          {/* <Button 
            variant="ghost" 
            size="icon" 
            className="mr-2" 
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button> */}
          {/* <h1 className="text-2xl font-bold">Leads Management</h1> */}
        </div>
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <Select 
            value={selectedCampaignId} 
            onValueChange={handleCampaignChange}
            disabled={isLoading}
          >
            <SelectTrigger className="w-full md:w-[300px]">
              <SelectValue placeholder="Select a campaign" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((campaign) => (
                <SelectItem key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      
        {campaignInfo && (
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between p-4">
              <div>
                <h2 className="text-xl font-semibold">{campaignInfo?.name}</h2>
                <p className="text-sm text-muted-foreground">Campaign ID: {selectedCampaignId}</p>
              </div>
              <Badge variant={campaignInfo?.status === "ACTIVE" ? "success" : "outline"}>
                {campaignInfo?.status}
              </Badge>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 bg-primary/10">
                  <AvatarFallback className="bg-primary/10 text-primary">
                    <User className="h-5 w-5" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm text-muted-foreground">Total Leads</p>
                  <p className="text-xl font-medium">{leadsdata?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="relative w-full md:w-auto md:flex-1 max-w-md">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              className="pl-10"
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="flex items-center gap-2 w-full md:w-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>Newest first</DropdownMenuItem>
                <DropdownMenuItem>Oldest first</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button 
              variant="outline"
              onClick={handleDownload}
              disabled={leadsdata?.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
        
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {leadKeys.map((key, index) => (
                    <TableHead key={index} className="whitespace-nowrap">
                      {key.replace(/_/g, ' ')}
                    </TableHead>
                  ))}
                  <TableHead className="whitespace-nowrap">Date</TableHead>
                  <TableHead className="whitespace-nowrap">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={leadKeys.length + 2 || 2} className="text-center py-10">
                      Loading leads...
                    </TableCell>
                  </TableRow>
                ) : !selectedCampaignId ? (
                  <TableRow>
                    <TableCell colSpan={leadKeys.length + 2 || 2} className="text-center py-10">
                      Please select a campaign to view leads
                    </TableCell>
                  </TableRow>
                ) : searchTerm ? (
                  searchFilter()?.length > 0 ? (
                    searchFilter().map((lead, index) => (
                      <TableRow 
                        key={index} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleLeadClick(lead.id)}
                      >
                        {leadKeys.map((key, keyIndex) => (
                          <TableCell key={keyIndex} className="whitespace-nowrap">
                            {lead[key] || "-"}
                          </TableCell>
                        ))}
                        <TableCell className="whitespace-nowrap">
                          {formatDate(lead.created_time)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              variant={leadQualities.get(lead.id) === "hot" ? "default" : "outline"}
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                updateLeadQuality(lead.id, "hot");
                              }}
                              disabled={updatingLeads.has(lead.id)}
                              className={`${leadQualities.get(lead.id) === "hot" 
                                ? "bg-green-600 text-white hover:bg-green-700" 
                                : "text-green-600 hover:text-green-700 hover:bg-green-50"}`}
                            >
                              <ThumbsUp className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant={leadQualities.get(lead.id) === "irrelevent" ? "default" : "outline"}
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                updateLeadQuality(lead.id, "irrelevent");
                              }}
                              disabled={updatingLeads.has(lead.id)}
                              className={`${leadQualities.get(lead.id) === "irrelevent" 
                                ? "bg-red-600 text-white hover:bg-red-700" 
                                : "text-red-600 hover:text-red-700 hover:bg-red-50"}`}
                            >
                              <ThumbsDown className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                openMessageModal(lead);
                              }}
                              className="whitespace-nowrap"
                            >
                              <FaWhatsapp className="h-4 w-4 mr-2" />
                              WhatsApp
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={leadKeys.length + 2 || 2} className="text-center py-10">
                        No leads found for "{searchTerm}"
                      </TableCell>
                    </TableRow>
                  )
                ) : leadsdata?.length > 0 ? (
                  leadsdata.map((lead, index) => (
                    <TableRow 
                      key={index}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleLeadClick(lead.id)}
                    >
                      {leadKeys.map((key, keyIndex) => (
                        <TableCell key={keyIndex} className="whitespace-nowrap">
                          {lead[key] || "-"}
                        </TableCell>
                      ))}
                      <TableCell className="whitespace-nowrap">
                        {formatDate(lead.created_time)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            variant={leadQualities.get(lead.id) === "hot" ? "default" : "outline"}
                            size="sm" 
                            onClick={(e) => {
                              e.stopPropagation();
                              updateLeadQuality(lead.id, "hot");
                            }}
                            disabled={updatingLeads.has(lead.id)}
                            className={`${leadQualities.get(lead.id) === "hot" 
                              ? "bg-green-600 text-white hover:bg-green-700" 
                              : "text-green-600 hover:text-green-700 hover:bg-green-50"}`}
                          >
                            <ThumbsUp className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant={leadQualities.get(lead.id) === "irrelevent" ? "default" : "outline"}
                            size="sm" 
                            onClick={(e) => {
                              e.stopPropagation();
                              updateLeadQuality(lead.id, "irrelevent");
                            }}
                            disabled={updatingLeads.has(lead.id)}
                            className={`${leadQualities.get(lead.id) === "irrelevent" 
                              ? "bg-red-600 text-white hover:bg-red-700" 
                              : "text-red-600 hover:text-red-700 hover:bg-red-50"}`}
                          >
                            <ThumbsDown className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={(e) => {
                              e.stopPropagation();
                              openMessageModal(lead);
                            }}
                            className="whitespace-nowrap"
                          >
                            <FaWhatsapp className="h-4 w-4 mr-2" />
                            WhatsApp
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={leadKeys.length + 2 || 2} className="text-center py-10">
                      No leads found for this campaign
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </main>
      
      {/* WhatsApp Message Modal */}
      {isMessageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg shadow-lg w-full max-w-md p-6 mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Send WhatsApp Message</h3>
              <Button variant="ghost" size="icon" onClick={closeMessageModal}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {messageStatus.error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md mb-4">
                {messageStatus.error}
              </div>
            )}
            
            {messageStatus.success && (
              <div className="bg-green-100 text-green-800 text-sm p-3 rounded-md mb-4">
                Message sent successfully!
              </div>
            )}
            
            <div className="space-y-4">
              {selectedLead && (
                <div className="bg-muted/50 p-3 rounded-md">
                  <h4 className="text-sm font-medium mb-2">Lead Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(selectedLead)
                      .filter(([key]) => !['id', 'created_time', 'comment'].includes(key))
                      .slice(0, 4)
                      .map(([key, value]) => (
                        <div key={key}>
                          <p className="text-muted-foreground">{key.replace(/_/g, ' ')}</p>
                          <p className="font-medium">{value || '-'}</p>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
              
              <div>
                <label htmlFor="whatsappTemplate" className="block text-sm font-medium mb-1">
                  Select Template
                </label>
                <Select 
                  value={selectedTemplate} 
                  onValueChange={setSelectedTemplate}
                  disabled={templates.length === 0}
                >
                  <SelectTrigger id="whatsappTemplate">
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {
                    templates
                    .filter(template => template.status !== "REJECTED")
                    .map((template) => (
                      <SelectItem key={template.id} value={template.name}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {templates.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    No templates available. Please create a template first.
                  </p>
                )}
              </div>
              
              {selectedTemplate && templates.length > 0 && (
                <div className="bg-muted/50 p-3 rounded-md">
                  <h4 className="text-sm font-medium mb-2">Template Preview</h4>
                  <div className="space-y-2">
                    {templates
                      .find(t => t.name === selectedTemplate)
                      ?.components
                      ?.map((component, index) => (
                        <div key={index}>
                          <p className="text-xs text-muted-foreground">{component.type}</p>
                          <p className="text-sm">{component.text}</p>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-3 mt-6">
                <Button type="button" variant="outline" onClick={closeMessageModal}>
                  Cancel
                </Button>
                <Button 
                  onClick={sendWhatsAppMessage}
                  disabled={!selectedTemplate || templates.length === 0 || sendingMessage || messageStatus.success}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {sendingMessage ? (
                    "Sending..."
                  ) : messageStatus.success ? (
                    "Sent!"
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Details Sidebar */}
      {isSidebarOpen && selectedLeadDetail && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 " 
            onClick={closeSidebar}
          ></div>
          
          {/* Sidebar */}
          <div className="ml-auto w-full max-w-md bg-background shadow-lg h-full overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  {selectedLeadDetail.field_data?.find(field => field.name === 'full_name')?.values[0] && (
                    <h2 className="text-2xl font-bold mb-2">
                      {selectedLeadDetail.field_data.find(field => field.name === 'full_name').values[0]}
                    </h2>
                  )}
                  
                  {/* Contact Info */}
                  <div className="space-y-2">
                    {selectedLeadDetail.field_data?.find(field => field.name === 'phone_number')?.values[0] && (
                      <div>
                        <span className="text-sm text-muted-foreground">Contact Number: </span>
                        <span className="text-purple-600 font-medium">
                          {selectedLeadDetail.field_data.find(field => field.name === 'phone_number').values[0]}
                        </span>
                      </div>
                    )}
                    
                    {selectedLeadDetail.field_data?.find(field => field.name === 'email')?.values[0] && (
                      <div>
                        <span className="text-sm text-muted-foreground">Email: </span>
                        <span className="text-purple-600 font-medium">
                          {selectedLeadDetail.field_data.find(field => field.name === 'email').values[0]}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <Button variant="ghost" size="icon" onClick={closeSidebar}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Divider */}
              <hr className="border-muted mb-6" />

              {/* Form Answers */}
              <div>
                <h3 className="text-xl font-bold mb-4">Form Answer</h3>
                
                <div className="space-y-4">
                  {selectedLeadDetail.field_data?.map((field, index) => {
                    // Skip the main contact fields as they're shown in header
                    if (['full_name', 'phone_number', 'email'].includes(field.name)) {
                      return null;
                    }
                    
                    return (
                      <div key={index} className="space-y-2">
                        <div>
                          <span className="text-sm text-muted-foreground font-medium">
                            Ques. {formatFieldName(field.name)}?
                          </span>
                        </div>
                        <div>
                          <span className="font-semibold">
                            Ans. {field.values[0] || '-'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* <BottomNavbar /> */}
    </div>
  );
}
